import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  FolderOpen,
  Clock,
  CheckCircle,
  Pause,
  XCircle,
  Calendar,
  User,
  DollarSign,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Project {
  id: string;
  name: string;
  reference_code?: string;
  customer_id?: string;
  customer?: { name: string } | null;
  description?: string;
  status: string;
  start_date?: string;
  end_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  budget?: number;
  is_active?: boolean;
}

interface ProjectKanbanViewProps {
  projects: Project[];
  onSelectProject: (id: string) => void;
}

interface KanbanColumn {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  items: Project[];
}

const STATUS_CONFIG: Record<string, { title: string; icon: React.ReactNode; color: string; bgColor: string }> = {
  pending: {
    title: "Pendiente",
    icon: <Clock className="w-4 h-4" />,
    color: "text-yellow-600",
    bgColor: "bg-yellow-500/10 border-yellow-500/30",
  },
  active: {
    title: "En Progreso",
    icon: <FolderOpen className="w-4 h-4" />,
    color: "text-blue-600",
    bgColor: "bg-blue-500/10 border-blue-500/30",
  },
  in_progress: {
    title: "En Progreso",
    icon: <FolderOpen className="w-4 h-4" />,
    color: "text-blue-600",
    bgColor: "bg-blue-500/10 border-blue-500/30",
  },
  on_hold: {
    title: "En Espera",
    icon: <Pause className="w-4 h-4" />,
    color: "text-orange-600",
    bgColor: "bg-orange-500/10 border-orange-500/30",
  },
  completed: {
    title: "Completado",
    icon: <CheckCircle className="w-4 h-4" />,
    color: "text-green-600",
    bgColor: "bg-green-500/10 border-green-500/30",
  },
  cancelled: {
    title: "Cancelado",
    icon: <XCircle className="w-4 h-4" />,
    color: "text-red-600",
    bgColor: "bg-red-500/10 border-red-500/30",
  },
};

export function ProjectKanbanView({ projects, onSelectProject }: ProjectKanbanViewProps) {
  const columns = useMemo<KanbanColumn[]>(() => {
    const grouped: Record<string, Project[]> = {};

    projects.forEach((project) => {
      const status = project.status || "pending";
      if (!grouped[status]) {
        grouped[status] = [];
      }
      grouped[status].push(project);
    });

    // Orden de columnas
    const statusOrder = ["pending", "active", "in_progress", "on_hold", "completed", "cancelled"];

    return statusOrder
      .filter((status) => grouped[status]?.length > 0 || ["pending", "active", "completed"].includes(status))
      .map((status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
        return {
          id: status,
          title: config.title,
          icon: config.icon,
          color: config.color,
          bgColor: config.bgColor,
          items: grouped[status] || [],
        };
      });
  }, [projects]);

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = end.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <ScrollArea className="w-full">
      <div className="flex gap-4 pb-4 min-w-max">
        {columns.map((column) => (
          <div
            key={column.id}
            className={cn("flex-shrink-0 w-80 rounded-lg border-2", column.bgColor)}
          >
            <div className={cn("flex items-center gap-2 p-3 border-b", column.color)}>
              {column.icon}
              <h3 className="font-semibold text-sm">{column.title}</h3>
              <Badge variant="secondary" className="ml-auto">
                {column.items.length}
              </Badge>
            </div>

            <ScrollArea className="h-[500px]">
              <div className="p-2 space-y-2">
                {column.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Sin proyectos
                  </div>
                ) : (
                  column.items.map((project) => {
                    const daysRemaining = project.end_date
                      ? getDaysRemaining(project.end_date)
                      : null;
                    const isOverdue = daysRemaining !== null && daysRemaining < 0;
                    const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;

                    return (
                      <Card
                        key={project.id}
                        className="cursor-pointer hover:shadow-md transition-shadow bg-card"
                        onClick={() => onSelectProject(project.id)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-medium text-sm line-clamp-2">
                              {project.name}
                            </h4>
                            {!project.is_active && (
                              <Badge variant="secondary" className="text-xs shrink-0">
                                Inactivo
                              </Badge>
                            )}
                          </div>

                          {project.reference_code && (
                            <p className="text-xs text-muted-foreground mb-2">
                              {project.reference_code}
                            </p>
                          )}

                          {project.customer && (
                            <div className="flex items-center gap-2 mb-2">
                              <Avatar className="h-5 w-5">
                                <AvatarFallback className="text-[10px]">
                                  {project.customer.name?.charAt(0) || "C"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-xs text-muted-foreground truncate">
                                {project.customer.name}
                              </span>
                            </div>
                          )}

                          {project.end_date && (
                            <div
                              className={cn(
                                "flex items-center gap-1 text-xs mb-2",
                                isOverdue
                                  ? "text-red-600"
                                  : isUrgent
                                  ? "text-yellow-600"
                                  : "text-muted-foreground"
                              )}
                            >
                              <Calendar className="w-3 h-3" />
                              {isOverdue
                                ? `Vencido hace ${Math.abs(daysRemaining!)} días`
                                : daysRemaining === 0
                                ? "Vence hoy"
                                : `Vence en ${daysRemaining} días`}
                            </div>
                          )}

                          {project.budget && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <DollarSign className="w-3 h-3" />
                              {project.budget.toLocaleString("es-ES", {
                                style: "currency",
                                currency: "EUR",
                              })}
                            </div>
                          )}

                          {project.estimated_hours && (
                            <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t">
                              <span className="text-muted-foreground">Horas:</span>
                              <span className="font-medium">
                                {project.actual_hours || 0} / {project.estimated_hours}
                              </span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
